/**
 * visitorRules — whether something is watching from the trees, and how near.
 *
 * Phase 5 of docs/design/single-scene-game-spec.md: "something in the dark
 * responds to the light". The spec lists the scene's hidden layers as "what the
 * dark holds, what the light draws in" — this is both at once, and the
 * direction matters enormously.
 *
 * It is DRAWN to the fire, not driven off by it. A visitor that crept closer as
 * the light failed would be the same mechanic pointed at dread, and dread is
 * the one thing this design has already ruled out: loss is impossible, nothing
 * is lacking or imminent, and the research note in the spec is explicit that
 * scarcity-for-challenge fights scarcity-for-atmosphere (Kennedy's own
 * postmortem calls that collision his biggest mistake). So keeping the fire up
 * brings company, and letting it fall means the company quietly leaves. The
 * reward for tending the fire is that you are not alone.
 *
 * Nothing here is ever announced, and nothing can be interacted with. Looking
 * is free and stays free. A player who never glances at the treeline loses
 * nothing they can be told about; a player who does gets the one thing in the
 * game nobody mentioned.
 */

/** How near the visitor has come. */
export type VisitorStage = 'absent' | 'watching' | 'near';

export interface VisitorState {
  stage: VisitorStage;
  /** ms spent in conditions that would bring it closer. */
  drawnMs: number;
  /** ms spent in conditions that would send it away. */
  shyMs: number;
  /** 0..1 — how present it is, for fading in and out. */
  presence: number;
}

export interface VisitorInputs {
  /** 0..1 fire fuel. */
  fuel: number;
  /** 0..1 how much night is in effect. */
  darkness: number;
  /** Frame delta in ms. */
  deltaMs: number;
}

/**
 * It only comes out once the grove is genuinely dark.
 *
 * Note what this gates on: measured darkness, not the vigil's stage name. The
 * two are not the same thing and the difference is large. `darkness` is derived
 * from the ambient light curve, and it crosses this threshold at about t=45s —
 * three quarters of the way through the stage still *called* dusk, which runs
 * to t=180s. So the earliest the eyes can show is around t=67s, comfortably
 * inside dusk by name and unmistakably inside the dark by eye.
 *
 * That is deliberate. The creature responds to how dark it actually is, which
 * is the thing a player can see, rather than to a label on the world clock that
 * nothing in the scene displays. An earlier version of this comment claimed it
 * could not appear during dusk at all — true of the name, false of the code.
 */
export const VISITOR_MIN_DARKNESS = 0.82;

/** A fire this strong is worth approaching. */
export const VISITOR_DRAW_FUEL = 0.55;

/** Below this it loses interest and drifts back into the trees. */
export const VISITOR_SHY_FUEL = 0.38;

/** How long the fire must hold before anything shows at the treeline. */
export const VISITOR_APPEAR_MS = 22_000;

/** How much longer again before it comes properly close. */
export const VISITOR_APPROACH_MS = 40_000;

/** How long the fire must be failing before it withdraws a stage. */
export const VISITOR_WITHDRAW_MS = 9_000;

/** Seconds to fade in or out. Slow: it should never pop. */
const PRESENCE_TAU = 2.4;

export function initialVisitorState(): VisitorState {
  return { stage: 'absent', drawnMs: 0, shyMs: 0, presence: 0 };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Advance the visitor.
 *
 * Two separate accumulators rather than one signed counter, because the two
 * directions are not symmetric and should not be: it takes twenty-two seconds
 * of a good fire to bring anything out of the trees and nine of a failing one
 * to send it back. Being slow to arrive is what makes it feel like a thing that
 * decided to come, rather than a readout of the fuel gauge.
 */
export function stepVisitor(state: VisitorState, input: VisitorInputs): VisitorState {
  const { fuel, darkness, deltaMs } = input;
  const dark = darkness >= VISITOR_MIN_DARKNESS;
  const drawing = dark && fuel >= VISITOR_DRAW_FUEL;
  const shy = !dark || fuel < VISITOR_SHY_FUEL;

  let { stage, drawnMs, shyMs } = state;

  if (drawing) {
    drawnMs += deltaMs;
    shyMs = 0;
  } else if (shy) {
    shyMs += deltaMs;
    drawnMs = 0;
  } else {
    // Between the two thresholds: it neither approaches nor leaves. The gap is
    // deliberate — a fire hovering on a single threshold would make it
    // flicker in and out, which reads as a bug rather than as a creature.
    drawnMs = 0;
    shyMs = 0;
  }

  if (shy && shyMs >= VISITOR_WITHDRAW_MS) {
    stage = stage === 'near' ? 'watching' : 'absent';
    shyMs = 0;
  } else if (drawing) {
    if (stage === 'absent' && drawnMs >= VISITOR_APPEAR_MS) {
      stage = 'watching';
      drawnMs = 0;
    } else if (stage === 'watching' && drawnMs >= VISITOR_APPROACH_MS) {
      stage = 'near';
      drawnMs = 0;
    }
  }

  const target = stage === 'absent' ? 0 : 1;
  const k = 1 - Math.exp(-(deltaMs / 1000) / PRESENCE_TAU);
  const presence = clamp01(state.presence + (target - state.presence) * k);

  return { stage, drawnMs, shyMs, presence };
}

/**
 * How far out from the fire it sits, as a multiple of the light's reach.
 *
 * Watching, it sits past the edge of what the fire shows: two faint
 * reflections at the treeline and nothing else. Near, it comes to the rim of
 * the pool — visibly brighter and wider-set, but still outside the light
 * proper. Verified by eye at both stages; it is not meant to walk into the
 * clearing, and it never does.
 */
export function visitorDistanceFactor(stage: VisitorStage): number {
  switch (stage) {
    case 'near': return 0.72;
    case 'watching': return 1.12;
    default: return 1.4;
  }
}
